/**
 * Pure daily-window arithmetic. No Firebase imports, so it is unit-testable
 * and usable from any tier.
 */

import { TIMEZONE } from './constants';

/** Minutes past midnight for an "HH:mm" string. */
export function parseClock(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) throw new Error(`bad clock value: ${hhmm}`);
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) throw new Error(`bad clock value: ${hhmm}`);
  return hours * 60 + minutes;
}

/**
 * How far the named zone is ahead of UTC at a given instant, in ms.
 * Derived from Intl rather than a table, so DST is whatever the platform says.
 */
function zoneOffsetMs(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);

  const field = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');

  // Re-read the wall-clock reading as if it were UTC; the difference from the
  // real instant is the offset.
  const asUtc = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour') % 24,
    field('minute'),
    field('second'),
  );
  return asUtc - at.getTime();
}

/**
 * The UTC instant for a wall-clock time on a given local day.
 * Iterated once because the offset itself depends on the instant, which is
 * what breaks naive conversions on the two DST days each year.
 */
export function zonedTimeToUtc(
  dayKey: string,
  minutesPastMidnight: number,
  timeZone: string = TIMEZONE,
): Date {
  const parts = dayKey.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const hours = Math.floor(minutesPastMidnight / 60);
  const minutes = minutesPastMidnight % 60;
  const naive = Date.UTC(y, m - 1, d, hours, minutes, 0, 0);

  let instant = naive - zoneOffsetMs(new Date(naive), timeZone);
  instant = naive - zoneOffsetMs(new Date(instant), timeZone);
  return new Date(instant);
}

export interface DailyWindow {
  dailyWindowStart: string;
  dailyWindowEnd: string;
}

/**
 * A uniformly random instant inside today's window, to the second.
 * `random` is injectable so the choice is testable.
 */
export function pickDailyTime(
  dayKey: string,
  window: DailyWindow,
  random: () => number = Math.random,
  timeZone: string = TIMEZONE,
): Date {
  const start = parseClock(window.dailyWindowStart);
  const end = parseClock(window.dailyWindowEnd);
  if (end <= start) {
    throw new Error(
      `window end ${window.dailyWindowEnd} is not after start ${window.dailyWindowStart}`,
    );
  }

  const spanSeconds = (end - start) * 60;
  const offsetSeconds = Math.floor(random() * spanSeconds);
  const base = zonedTimeToUtc(dayKey, start, timeZone);
  return new Date(base.getTime() + offsetSeconds * 1000);
}

/**
 * Whole minutes a post is past the on-time window. Never negative: early is
 * simply on time.
 */
export function lateMinutes(postedAt: Date, windowEndsAt: Date): number {
  const diffMs = postedAt.getTime() - windowEndsAt.getTime();
  if (diffMs <= 0) return 0;
  return Math.floor(diffMs / 60000);
}
